import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { categories_list } from "../../Shared/List";
import { InputField } from "../../Shared/InputField"
import SelectField from "../../Shared/SelectField"
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const DailyReadingSummary = () => {
    const [errors, setErrors] = useState({});
    const [reading, setReading] = useState([]);
    const [title, setTitle] = useState('');
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
        by: 1,
    })
    const {fdate, tdate, by} = formData
    const fetchData = async (payload) => {
        setTitle(`Reading Daily Report as ${fdate} to ${tdate} by ${categories_list[formData.by - 1].name}`);
        setIsLoading(true)

        try {
            const response = await Server.post('/reports/daily-reading', payload)
            const categories = []
            categories_list.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setReading(response?.data);
            setByOptions(categories);
            setIsLoading(false);

            const output = response?.data.reduce((data, row) => {
                data.customers += parseInt(row.customers);
                data.previous_balance += parseFloat(row.previous_balance);
                data.usage += parseFloat(row.usage);
                data.debit += parseFloat(row.debit);
                return data;
            }, {customers: 0, previous_balance: 0.00, usage: 0, debit: 0.00, total: 0.00});
            setPinnedBottomRowData([
                {
                    customers: output.customers,
                    previous_balance: parseFloat(output.previous_balance).toFixed(2),
                    usage: parseFloat(output.usage).toFixed(2),
                    debit: parseFloat(output.debit).toFixed(2),
                    total: (output.previous_balance + output.debit).toFixed(2)
                }
            ]);

            const newUsageHeaderName = response?.data[0]?.branch?.unit;
            // Update only the "Usage" column's headerName
            const updatedColumnDefs = columnDefs.map(col => {
                return col.field === 'usage' ? { ...col, headerName: newUsageHeaderName }: col
            });
            setColumnDefs(updatedColumnDefs);

        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load reading summary')
            }
        }
    }

    useEffect(() => {
        if (location.state?.fdate && location.state?.tdate) {
            const matchedCategory = typeof location.state.by === 'string'
                ? categories_list.find(c => c.name.toLowerCase() === location.state.by.toLowerCase())
                : null;
            const byStr = matchedCategory?.name || categories_list[formData.by - 1].name;
            const byId = matchedCategory?.id || (typeof location.state.by === 'number' ? location.state.by : 1);
            setFormData({
                fdate: location.state.fdate,
                tdate: location.state.tdate,
                by: byId,
            });
            fetchData({ fdate: location.state.fdate, tdate: location.state.tdate, by: byStr });
        } else {
            //find category name with its index
            const by = categories_list[formData.by - 1].name
            fetchData({fdate, tdate, by})
        }
    }, [])

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onDoubleClick = (payload) => {
        navigate(`/daily-reading-detail`, { state: {data: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        //find category name with its index
        const by = categories_list[formData.by - 1].name
        const payload = {fdate, tdate, by}
        fetchData(payload)

    }

    const [columnDefs, setColumnDefs] = useState([
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customer",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Usage",
            field: "usage",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Previous Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ]);

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                        max={new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0]}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) =>{
                                if (e.node.rowPinned) return '';
                                if (e.data.customers < 1) return '';
                                onDoubleClick({fdate, tdate, public_id: e.data.public_id, name: e.data.name, by: categories_list[formData.by - 1].name.toLowerCase()})
                            }}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            suppressMenuHide={true}

                            rowData={reading}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default DailyReadingSummary